import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MuiButton from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { Background, MessageBox, Title, ErrorCategory, Description } from '../styled';
import type { ErrorCode, ErrorLocationState } from '../errorCodes';

const DEFAULT_CODE: ErrorCode = 'NOT_FOUND';

const codeToKeys = (code: ErrorCode) => {
  switch (code) {
    case 'MISSING_PARAMS':
      return {
        title: 'error_message:codes.missing_params.title',
        category: 'error_message:codes.missing_params.category',
        description: 'error_message:codes.missing_params.description',
      } as const;
    case 'INVALID_CLIENT':
      return {
        title: 'error_message:codes.invalid_client.title',
        category: 'error_message:codes.invalid_client.category',
        description: 'error_message:codes.invalid_client.description',
      } as const;
    case 'NOT_FOUND':
      return {
        title: 'error_message:codes.not_found.title',
        category: 'error_message:codes.not_found.category',
        description: 'error_message:codes.not_found.description',
      } as const;
    case 'UNKNOWN':
      return {
        title: 'error_message:codes.unknown.title',
        category: 'error_message:codes.unknown.category',
        description: 'error_message:codes.unknown.description',
      } as const;
  }
};

const Error = () => {
  const { t } = useTranslation(['error_message']);
  const location = useLocation();
  const state = (location.state as ErrorLocationState | null) ?? {};
  const code = state.code ?? DEFAULT_CODE;
  const returnUri = state.returnUri;

  const keys = codeToKeys(code);
  const description = t(keys.description);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [code]);

  const handleReturn = useCallback(() => {
    if (returnUri) {
      window.location.href = returnUri;
    }
  }, [returnUri]);

  return (
    <Background>
      <MessageBox>
        <Title>{t(keys.title)}</Title>
        <ErrorCategory>{t(keys.category)}</ErrorCategory>
        {description && <Description>{description}</Description>}
        <Stack direction="row" spacing={2} sx={{ marginTop: 3 }}>
          {returnUri && (
            <MuiButton variant="outlined" onClick={handleReturn}>
              {t('error_message:actions.return_to_app')}
            </MuiButton>
          )}
          <MuiButton variant="text" onClick={handleCopy}>
            {copied
              ? t('error_message:actions.copied')
              : t('error_message:actions.copy_error_code')}
          </MuiButton>
        </Stack>
      </MessageBox>
    </Background>
  );
};

export default Error;
